import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export interface OfflineSale {
  id: string;
  tenantId: string;
  invoiceData: any;
  lineItems: any[];
  timestamp: number;
  status: "pending" | "syncing" | "synced" | "failed";
  retryCount: number;
  error?: string;
}

export interface OfflineInventoryItem {
  id: string;
  tenantId: string;
  data: any;
  cachedAt: number;
}

export interface OfflineCustomer {
  id: string;
  tenantId: string;
  data: any;
  cachedAt: number;
}

interface OsirisBrainDB extends DBSchema {
  offline_sales: {
    key: string;
    value: OfflineSale;
    indexes: {
      "by-tenant": string;
      "by-status": string;
      "by-tenant-status": [string, string];
    };
  };
  offline_inventory: {
    key: string;
    value: OfflineInventoryItem;
    indexes: {
      "by-tenant": string;
      "by-cached-at": number;
    };
  };
  offline_customers: {
    key: string;
    value: OfflineCustomer;
    indexes: {
      "by-tenant": string;
      "by-cached-at": number;
    };
  };
}

const DB_NAME = "osirisbrain-offline";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<OsirisBrainDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<OsirisBrainDB>> {
  if (dbPromise) return dbPromise;

  dbPromise = openDB<OsirisBrainDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // offline_sales store
      if (!db.objectStoreNames.contains("offline_sales")) {
        const salesStore = db.createObjectStore("offline_sales", {
          keyPath: "id",
        });
        salesStore.createIndex("by-tenant", "tenantId");
        salesStore.createIndex("by-status", "status");
        salesStore.createIndex("by-tenant-status", ["tenantId", "status"]);
      }

      // offline_inventory store
      if (!db.objectStoreNames.contains("offline_inventory")) {
        const invStore = db.createObjectStore("offline_inventory", {
          keyPath: "id",
        });
        invStore.createIndex("by-tenant", "tenantId");
        invStore.createIndex("by-cached-at", "cachedAt");
      }

      // offline_customers store
      if (!db.objectStoreNames.contains("offline_customers")) {
        const custStore = db.createObjectStore("offline_customers", {
          keyPath: "id",
        });
        custStore.createIndex("by-tenant", "tenantId");
        custStore.createIndex("by-cached-at", "cachedAt");
      }
    },
  });

  return dbPromise;
}

export async function closeDB(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
}

export async function clearStore(
  storeName: "offline_sales" | "offline_inventory" | "offline_customers",
): Promise<void> {
  const db = await getDB();
  await db.clear(storeName);
}
